// Phase 25 iter-5b — KYC start page.
//
// Mostra o estado actual + botón para iniciar (ou reintentar) a sesión
// DIDIT. O backend persiste a info; aquí só facemos o redirect e
// dependemos do polling de useKycStatus para que a UI se actualice
// cando o webhook chega.

import { CheckCircle2, ExternalLink, ShieldCheck, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { startKycSession } from '../api/kyc';
import { describeAxiosError } from '../api/errors';
import InfoCallout from '../components/InfoCallout';
import ContentPage from '../components/ui/ContentPage';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useKycStatus } from '../hooks/useKycStatus';

const STATUS_COPY: Record<
  string,
  { label: string; tone: 'neutral' | 'pending' | 'success' | 'error'; body: string }
> = {
  NONE: {
    label: 'Sen iniciar',
    tone: 'neutral',
    body: 'Aínda non comezaches a verificación. Necesítase para algunhas accións (reservar animais, asinar como veterinario, etc.).',
  },
  PENDING: {
    label: 'En curso',
    tone: 'pending',
    body: 'Comezaches a verificación pero non chegaron os documentos. Completa o proceso na xanela aberta, ou volve premer "Iniciar verificación" para retomar.',
  },
  SUBMITTED: {
    label: 'En revisión',
    tone: 'pending',
    body: 'Recibimos os teus documentos. Estámolos a procesar — normalmente tarda menos dunha hora.',
  },
  VERIFIED: {
    label: 'Verificado',
    tone: 'success',
    body: 'A túa identidade está verificada. Xa podes operar sen restricións segundo o teu rol.',
  },
  REJECTED: {
    label: 'Rexeitado',
    tone: 'error',
    body: 'A verificación non se completou correctamente. Podes reintentar; revisa que os documentos sexan lexibles e válidos.',
  },
};

export default function KycPage(): JSX.Element {
  const { accessToken } = useAuthContext();
  const { addToast } = useToast();
  const { status, loading, error } = useKycStatus();
  const [starting, setStarting] = useState(false);

  if (!accessToken) {
    return <Navigate to="/marketplace/login?next=/marketplace/kyc" replace />;
  }

  async function handleStart() {
    setStarting(true);
    try {
      const session = await startKycSession();
      // Mobile browsers (iOS Safari, Chrome móbil) bloquean `window.open`
      // chamado tras un `await` porque o user gesture context xa expirou.
      // Usamos `location.assign` para que o flow funcione en ambos
      // contextos — DIDIT devolve ao DIDIT_CALLBACK_URL ao rematar e
      // o useKycStatus polling actualiza o estado dende aí.
      addToast('Abrindo a verificación…', 'default');
      window.location.assign(session.verification_url);
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido iniciar a verificación.'), 'error');
      setStarting(false);
    }
  }

  const copy = status ? (STATUS_COPY[status.status] ?? STATUS_COPY.NONE) : null;
  const canStart = !status || ['NONE', 'PENDING', 'REJECTED'].includes(status.status);

  return (
    <ContentPage
      title="Verificación de identidade"
      intro="Para algunhas accións (reservar animais, asinar como veterinario, recoller envíos) necesitamos confirmar a túa identidade cun provedor europeo certificado para KYC."
      category="Conta"
    >
      <div className="not-prose mb-4">
        <InfoCallout
          storageKey="kyc-page-why"
          defaultOpen={true}
          icon={<ShieldCheck size={18} className="text-rauxa" />}
          title="Por que verificamos a túa identidade?"
          body="É requisito legal para asinar contratos electrónicos válidos (eIDAS firma avanzada). Tamén protéxete contra fraudes. Usamos DIDIT para verificar o DNI/NIE — os datos non se almacenan en blockchain, só un hash de verificación. Recibirás un email cando se complete a revisión."
        />
      </div>

      {loading && !status && (
        <div className="flex items-center gap-3 text-sm text-muted">
          <Spinner size={18} /> Cargando estado…
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-danger bg-red-50 text-danger text-sm p-4 not-prose">
          {error}
        </div>
      )}

      {status && copy && (
        <section className="rounded-2xl border border-border bg-white p-6 not-prose flex items-start gap-4">
          <StatusIcon tone={copy.tone} />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-dark">{copy.label}</h3>
              <span className="text-[10px] uppercase tracking-wide font-bold text-muted bg-subtle px-2 py-0.5 rounded-full">
                Aplícase a:{' '}
                {status.applicable_roles.length > 0
                  ? status.applicable_roles.join(', ')
                  : status.role}
              </span>
              {status.required ? (
                <span className="text-[10px] uppercase tracking-wide font-bold text-rauxa bg-rauxa/10 px-2 py-0.5 rounded-full">
                  Obrigatorio
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wide font-bold text-muted bg-subtle px-2 py-0.5 rounded-full">
                  Opcional
                </span>
              )}
            </div>
            {status.applicable_roles.length > 1 && (
              <p className="text-xs text-muted mt-1">
                Un único KYC cobre todos os teus roles activos.
              </p>
            )}
            <p className="text-sm text-muted mt-2 leading-relaxed">{copy.body}</p>
            {status.verified_at && (
              <p className="text-xs text-muted mt-2">
                Verificado o {new Date(status.verified_at).toLocaleString('gl-ES')}.
              </p>
            )}

            {status.can_initiate && canStart && (
              <button
                type="button"
                onClick={() => void handleStart()}
                disabled={starting}
                className="btn-primary mt-4 inline-flex items-center gap-2 disabled:opacity-60"
              >
                {starting ? (
                  <Spinner size={16} className="mx-auto" />
                ) : (
                  <>
                    {status.status === 'PENDING'
                      ? 'Retomar verificación'
                      : status.status === 'REJECTED'
                        ? 'Reintentar verificación'
                        : 'Iniciar verificación'}
                    <ExternalLink size={14} />
                  </>
                )}
              </button>
            )}
            {status.status === 'PENDING' && (
              <p className="text-xs text-muted mt-3 leading-relaxed">
                Se pechaches a xanela sen rematar, simplemente preme <em>Retomar verificación</em>{' '}
                para empezar unha nova sesión.
              </p>
            )}

            {!status.can_initiate && status.status !== 'VERIFIED' && (
              <p className="text-xs text-muted mt-3">
                O teu rol actual non permite iniciar KYC aínda. Contáctanos se cres que é un erro.
              </p>
            )}
          </div>
        </section>
      )}

      <h2>Que pasa cando inicio a verificación?</h2>
      <ol>
        <li>Premes "Iniciar verificación" e ábrese a verificación nunha nova pestana.</li>
        <li>Sigues os pasos: foto do DNI, selfie de comparación, prova de vida.</li>
        <li>Cando se confirma, esta páxina actualízase automaticamente (cada 30 s).</li>
        <li>O teu rol fica desbloqueado para todas as accións dispoñibles.</li>
      </ol>

      <h2>Que datos comparte o provedor con LeyroChain?</h2>
      <p>
        Só recibimos a decisión final (aprobado / denegado) máis un identificador opaco. Os teus
        documentos persoais quédanse no provedor certificado, non nos servidores de LeyroChain.
        Detalle en <a href="/marketplace/privacidade">Política de privacidade</a>.
      </p>
    </ContentPage>
  );
}

function StatusIcon({ tone }: { tone: 'neutral' | 'pending' | 'success' | 'error' }) {
  if (tone === 'success') return <CheckCircle2 size={28} className="text-rauxa shrink-0 mt-0.5" />;
  if (tone === 'error') return <XCircle size={28} className="text-danger shrink-0 mt-0.5" />;
  if (tone === 'pending') return <Spinner size={26} className="!border-t-rauxa shrink-0 mt-0.5" />;
  return <ShieldCheck size={28} className="text-muted shrink-0 mt-0.5" />;
}
